import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ChatMessageProps } from "@/types";
import { Clock, User } from "lucide-react";
import React from "react";
import UserMessageActions from "./components/UserMessageActions";
import UserPartsGrid from "./components/UserPartsGrid";
import UserVehicleChip from "./components/UserVehicleChip";
import "./styles/ChatMessageUser.css";
import { userService } from "@/services/UserService";

const ChatMessageUser: React.FC<ChatMessageProps> = React.memo(
  ({ message, showTimestamp = true, compact = false }) => {
    const formatTime = React.useMemo(
      () => (date: Date) => {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      []
    );

    const userData = React.useMemo(() => userService.getUserData(), []);

    const hasPartsData = React.useMemo(
      () =>
        (message.pcdbParts && message.pcdbParts.length > 0) ||
        (message.vmrsParts && message.vmrsParts.length > 0),
      [message.pcdbParts, message.vmrsParts]
    );

    const formattedTimestamp = React.useMemo(
      () => formatTime(message.timestamp),
      [message.timestamp, formatTime]
    );

    return (
      <div className={`chat-message-user-modern ${compact ? "compact" : ""}`}>
        <div className="user-message-container-modern">
          <div className="user-message-content-modern">
            <div className="user-message-header-modern">
              <div className="user-sender-info-modern">
                <span className="user-sender-name-modern">You</span>
                {showTimestamp && (
                  <div className="user-timestamp-wrapper-modern">
                    <Clock size={12} className="timestamp-icon-modern" />
                    <span className="user-timestamp-modern">
                      {formattedTimestamp}
                    </span>
                  </div>
                )}
              </div>
              <UserMessageActions message={message} />
            </div>

            {message.vehicleData && (
              <UserVehicleChip
                vehicleData={message.vehicleData}
                compact={compact}
              />
            )}

            <div className="user-message-bubble-modern">
              <div className="user-message-text-modern">{message.content}</div>
            </div>

            {hasPartsData && (
              <UserPartsGrid
                pcdbParts={message.pcdbParts}
                vmrsParts={message.vmrsParts}
                compact={compact}
              />
            )}
          </div>

          <div className="user-avatar-wrapper-modern">
            <Avatar className="user-message-avatar-modern">
              <AvatarFallback className="user-avatar-fallback-modern">
                {userData?.initials}
              </AvatarFallback>
            </Avatar>
            <div className="user-status-indicator-modern"></div>
          </div>
        </div>
      </div>
    );
  }
);

ChatMessageUser.displayName = "ChatMessageUser";

export default ChatMessageUser;
